import React, { useContext, useState, useLayoutEffect, useEffect, useRef } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { PlusCircleIcon, PlusIcon, InfoCircleFilledIcon, PeopleIcon } from '@vlabs/icons';
import { ListItem } from '@vlabs/shared/components/list-item/ListItem';
import { Modal, Control, AccountMenu, AccountMenuButton } from '@vlabs/uikit';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { QueueContext } from '../queue-context/QueueContext';
import st from './CreateGroupModal.module.sass';

// функция вычисляет какие потоки были удалены, а какие добавлены в группу
function diffStreams(oldList, newList) {
  const added = newList.filter(
    (s) => !oldList.some((o) => o.stream_id === s.stream_id),
  );
  const removed = oldList.filter(
    (o) => !newList.some((s) => s.stream_id === o.stream_id),
  );
  return { added, removed };
}

// функция обновляет поток с новой зоной группы
async function updateStreamZoneGroup(streamId, description, zoneGroup) {
  const descObj = JSON.parse(description);
  descObj.zone_group = zoneGroup;
  const updatedDesc = JSON.stringify(descObj);

  const streamData = await apiContainer.lunaClient.streams.get(streamId);

  return apiContainer.lunaClient.streams.edit({
    streamId,
    payload: { ...streamData, description: updatedDesc },
  });
}

export function CreateGroupModal({
  isOpen,
  onClose,
  groupName,
}) {
  const { t } = useTranslation();
  const { streams, fetchStreams, openStreamModal } = useContext(QueueContext);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  const isEdit = !!groupName;
  const { register, handleSubmit, setValue, watch, reset } = useForm(
    {
      values: {
        name: isEdit ? groupName : '',
        streams: isEdit ? streams[groupName] : [],
      },
    });

  useLayoutEffect(() => {
    if (isDropDownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.pageYOffset,
        left: rect.left + window.pageXOffset,
      });
    }
  }, [isDropDownOpen]);

  const createOptionsForDropdown = () => {
    return Object.entries(streams)
      .filter(([g]) => g !== groupName)
      .flatMap(([, arr]) => arr);
  };

  useEffect(() => {
    if (!isOpen) return;

    reset({
      name: isEdit ? groupName : '',
      streams: isEdit && streams[groupName]
        ? streams[groupName]
        : [],
    });

    setDropdownOptions(createOptionsForDropdown());
  }, [groupName, isEdit, isOpen, reset, streams]);

  const selectedStreams = watch('streams');
  const onSubmit = async ({ name: newGroupName, streams: newStreams }) => {
    const oldStreams = isEdit ? streams[groupName] || [] : [];
    const { added, removed } = diffStreams(oldStreams, newStreams);

    const remaining = oldStreams.filter(
      (o) => newStreams.some((s) => s.stream_id === o.stream_id)
        && oldStreams.some((s) => s.stream_id === o.stream_id),
    );

    try {
      // Если потоки в группе не поменялись,- то просто обновляем потоки с новым именем группы
      if (
        isEdit
        && newGroupName !== groupName
        && added.length === 0
        && removed.length === 0
      ) {
        await Promise.all(
          oldStreams.map((s) => updateStreamZoneGroup(s.stream_id, s.description, newGroupName),
          ),
        );
      } else {
        // Если потоки в группе поменялись,- то новым назначаем новое имя, удалённым - null
        const tasks = [
          ...added.map((s) => updateStreamZoneGroup(s.stream_id, s.description, newGroupName),
          ),
          ...removed.map((s) => updateStreamZoneGroup(s.stream_id, s.description, null),
          ),
        ];
        // если поменялось название - то оставшимся группам устанавливаем новое имя
        if (isEdit && newGroupName !== groupName) {
          tasks.push(
            ...remaining.map((s) => updateStreamZoneGroup(s.stream_id, s.description, newGroupName),
            ),
          );
        }

        await Promise.all(tasks);
      }

      await fetchStreams();
      onClose();
    } catch (error) {
      console.error('Ошибка при сохранении группы:', error);
      toast.error(t('queue:queue page.create group modal.error'));
    }
  };

  const handleAddStream = (streamToAdd) => {
    setValue('streams', [...selectedStreams, streamToAdd]);
    setDropdownOptions((prev) => prev.filter((s) => s.stream_id !== streamToAdd.stream_id));
    setIsDropDownOpen(false);
  };

  const handleDeleteStream = (streamToDelete) => {
    setValue('streams', selectedStreams.filter((stream) => stream.stream_id !== streamToDelete.stream_id));
    setDropdownOptions((prev) => {
      if (prev.some((s) => s.stream_id === streamToDelete.stream_id)) {
        return prev;
      }
      return ([...prev, streamToDelete]);
    });
  };

  const handleCreateStream = () => {
    onClose();
    openStreamModal(watch('name'));
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      size="small"
      data-testid="createQueueGroupModal"
    >
      <form
        className={st.ModalContent}
        onSubmit={handleSubmit(onSubmit)}
      >
        {
          isEdit
            ? <div className={st.Title}>{t('queue:queue page.create group modal.title.edit')}</div>
            : <div className={st.Title}>{t('queue:queue page.create group modal.title.create')}</div>
        }

        <Control.Input
          {...register('name', {
            required: true,
          })}
          className={st.GroupNameInput}
          id="groupNameInputInCreateGroupModal"
          data-testid="queueGroupNameInput"
          label={t('queue:queue page.create group modal.group name input.label')}
        />

        <div className={st.ListWithButtons}>
          {
            selectedStreams.length > 0
            && (
              <div className={st.QueuesList}>
                <div className={st.Subtitle}>{t('queue:queue page.create group modal.subtitle')}</div>
                {
                  selectedStreams.map((stream) => (
                    <ListItem
                      icon={<PeopleIcon />}
                      key={stream.stream_id}
                      label={stream.name}
                      onClick={() => handleDeleteStream(stream)}
                    />
                  ))
                }
              </div>
            )
          }

          {!isEdit && selectedStreams.length < 1 && (
            <div className={st.Notification}>
              <InfoCircleFilledIcon />
              <span>{t('queue:queue page.create group modal.notification')}</span>
            </div>
          )}

          <div className={st.GroupsListButtonsSection}>
            <Control.Button
              data-testid="CreateGroupModal_addStreamButton"
              icon={<PlusIcon />}
              onClick={() => setIsDropDownOpen((prev) => !prev)}
              ref={buttonRef}
              size="small"
              variant="flat"
            >
              {t('queue:queue page.create group modal.buttons.add stream button')}
            </Control.Button>

            {
              createPortal((
                <div
                  className={st.Menu}
                  style={{
                    top: `${dropdownPos.top}px`,
                    left: `${dropdownPos.left}px`,
                  }}
                >
                  <AccountMenu
                    isOpened={isDropDownOpen}
                  >
                    {dropdownOptions?.map((stream) => (
                      <AccountMenuButton
                        data-testid="CreateGroupModal_dropdownItem"
                        onClick={() => handleAddStream(stream)}
                      >
                        {stream.name}
                      </AccountMenuButton>
                    ))}
                  </AccountMenu>
                </div>), document.getElementById('root'))
            }

            <Control.Button
              data-testid="CreateGroupModal_createStreamButton"
              icon={<PlusCircleIcon />}
              onClick={handleCreateStream}
              size="small"
              variant="flat"
            >
              {t('queue:queue page.create group modal.buttons.create stream button')}
            </Control.Button>
          </div>
        </div>
        <div className={st.GeneralButtonsSection}>
          <Control.Button
            data-testid="CreateGroupModal_submitButton"
            disabled={!selectedStreams.length}
            type="submit"
          >
            {t('queue:queue page.create group modal.buttons.save button')}
          </Control.Button>
          <Control.Button
            data-testid="CreateGroupModal_cancelButton"
            kind="secondary"
            onClick={onClose}
            variant="outlined"
          >
            {t('queue:queue page.create group modal.buttons.cancel button')}
          </Control.Button>
        </div>
      </form>
    </Modal>
  );
}
